// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.
import { Tooltip } from '@mui/material';
import {
  ChartContainer,
  DonutChart,
  Table,
  type PopupsName,
} from '@shared/ui-kit/components';
import { classNames, SERIES_COLORS, formatToPercent } from '@shared/ui-kit/utils';
import { type FC, type ReactNode, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

const EXPOSURE_KEY_MAP = {
  Equity: 'common.equity',
  'Fixed Income': 'common.fixed-income',
  Alternative: 'common.alternative',
  Stock: 'common.stock',
  Fund: 'common.fund',
  Other: 'common.other',
};

interface DonutChartContainerProps {
  name: string | ReactNode;
  exposure: string[];
  weight: number[];
  testId?: string;
  popupName?: PopupsName;
  noDataMessage?: string;
  legend?: boolean;
  sort?: boolean;
  divider?: boolean;
  /**
   * Smaller donut ring and a taller legend area (e.g. beside a historical chart) so
   * the breakdown list stays usable.
   */
  compactLayout?: boolean;

  footer?: ReactNode;
}

export const DonutChartContainer: FC<DonutChartContainerProps> = ({
  name,
  exposure,
  weight,
  testId,
  popupName,
  noDataMessage,
  legend = true,
  sort = true,
  divider = true,
  compactLayout = false,
  footer,
}) => {
  const { t } = useTranslation();

  // Default message translated
  const defaultNoDataMessage = t('dashboard.there-is-no-data-available', {
    ns: 'dashboard',
  });

  const data = useMemo(
    () =>
      exposure
        .map((category, index) => {
          const translatedLabel = EXPOSURE_KEY_MAP[
            category as keyof typeof EXPOSURE_KEY_MAP
          ]
            ? t(EXPOSURE_KEY_MAP[category as keyof typeof EXPOSURE_KEY_MAP], {
                ns: 'common',
              })
            : category;

          return {
            id: category,
            label: translatedLabel,
            value: weight[index],
          };
        })
        .sort((a, b) => (sort ? b.value - a.value : 0)),
    [exposure, weight, sort, t],
  );

  const hasData = data.some(item => item.value !== 0);

  return (
    <ChartContainer
      header={name}
      popupName={popupName}
      divider={divider}
      tooltip
      fullHeight={compactLayout}
      className={compactLayout ? 'h-full min-h-0' : undefined}
    >
      {hasData ? (
        <div
          className={classNames(
            'px-4 py-0',
            compactLayout
              ? 'flex min-h-0 flex-1 flex-col gap-2'
              : 'grid grid-cols-1 gap-0',
          )}
          data-testid={testId}
        >
          <div
            className={classNames(
              'relative shrink-0',
              compactLayout ? 'h-52' : 'h-64',
            )}
          >
            <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
              <span className="block w-full text-center !font-inter text-xl font-bold tabular-nums text-graph-text">
                {formatToPercent(data[0]?.value)}
              </span>
            </div>
            <DonutChart data={data} />
          </div>
          <div
            className={classNames(
              'overflow-y-auto px-2 aria-hidden:hidden',
              compactLayout ? 'mt-0 min-h-0 flex-1' : 'mt-2 max-h-[160px]',
            )}
            data-testid="chart-legend"
            aria-hidden={!legend}
          >
            {data.map(({ label, value }, index) => (
              <div key={index}>
                <div className="flex justify-between">
                  <div className="flex">
                    <div
                      className="mr-4 w-1"
                      style={{ backgroundColor: SERIES_COLORS[index] }}
                    />
                    <Tooltip title={label} placement="top">
                      <span className="max-w-44 truncate text-primary-medium">
                        {label}
                      </span>
                    </Tooltip>
                  </div>
                  <span className="!money-display mr-4 text-primary-medium">
                    <Table.Data value={value} format="percentage" />
                  </span>
                </div>
                <hr className="my-1.5 bg-border-sep" />
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div
          className="mx-auto mt-5 space-y-4 px-8 text-center text-border-dark "
          data-testid="no-data-message"
        >
          <p>{noDataMessage || defaultNoDataMessage}</p>
        </div>
      )}
      {footer ? (
        <div className="mt-2 flex justify-center pb-1">{footer}</div>
      ) : null}
    </ChartContainer>
  );
};
